import type { NewsletterResponse } from '../api/client';
import type { NewsletterAudience } from '../api/queries';

/**
 * The one vocabulary for the two lists, used by the composer, the sign-up
 * tags, the past-send line and the confirmation — so the same list reads the
 * same everywhere on the screen.
 *
 * The names are the public site's: "A letter from the lab" is the box on the
 * Support page; a study's subscribers pressed "Get notified when these open"
 * on that study's page and were promised that study's news and nothing else.
 */

export type AudienceStudy = NewsletterResponse['audiences']['studies'][number];

export const LETTER_NAME = 'A letter from the lab';
/** The short tag a sign-up row carries for the letter list. */
export const LETTER_TAG = 'Lab letter';

const joinNames = (names: string[]): string =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

const shortOf = (slug: string, studies: AudienceStudy[]): string =>
  studies.find((s) => s.slug === slug)?.short ?? slug;

/**
 * Who an audience is, as the end of a sentence: "…goes to 6 people:
 * subscribers of Young adult and Group psilocybin." Works after "To " as well.
 */
export function audienceLabel(kind: NewsletterAudience, slugs: string[], studies: AudienceStudy[]): string {
  if (kind === 'letter') return 'everyone who asked for the letter from the lab';
  if (kind === 'everyone') return 'everyone on both lists';
  return slugs.length ? `subscribers of ${joinNames(slugs.map((s) => shortOf(s, studies)))}` : 'subscribers of no study yet';
}

/** The tags a sign-up row carries: which lists it joined. */
export function listTags(sub: { labLetter: boolean; studies: string[] }, studies: AudienceStudy[]): string[] {
  const tags = sub.labLetter ? [LETTER_TAG] : [];
  for (const slug of sub.studies) tags.push(shortOf(slug, studies));
  return tags;
}

export const people = (n: number): string => (n === 1 ? 'person' : 'people');
